import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RegistrationCountdown } from "@/components/ladder/RegistrationCountdown";
import { wordmarkForSlug } from "@/lib/ladder-brands";

export type BannerLadder = {
  slug: string;
  name: string;
  registrationClosesAt: string | null;
};

/**
 * One piece of content in three places: the previous season's page, where stale registration links
 * now land; the signed-out landing page; and a signed-in player's home. Renders nothing once
 * registration has closed, so no caller needs a guard of its own.
 *
 * Laid out as one row from `sm` up (wordmark, countdown, button) and stacked on a phone.
 */
export const NextSeasonBanner = ({
  ladder,
  size = "lg",
}: {
  ladder: BannerLadder;
  size?: "sm" | "lg";
}) => {
  const closesAt = ladder.registrationClosesAt;
  const wordmark = wordmarkForSlug(ladder.slug);
  // The countdown holds the only timer. Without hearing from it, this guard is evaluated once and
  // a banner left open across the deadline keeps advertising a Register button indefinitely - the
  // digits vanish and the call to action stays.
  //
  // The flag records WHICH deadline expired rather than that one did. React keeps this instance
  // when the caller swaps in a different ladder - overlapping registration windows make that a
  // normal event - and a plain boolean would then hide a season that has not closed at all.
  const [expiredFor, setExpiredFor] = useState<string | null>(null);
  const expired = closesAt !== null && expiredFor === closesAt;
  if (expired || !closesAt || Date.now() > new Date(closesAt).getTime()) return null;

  return (
    <div className={`overflow-hidden rounded-xl bg-sanur-petrol ${size === "lg" ? "p-5" : "p-4"}`}>
      <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
        {wordmark ? (
          // Intrinsic size is declared so the layout does not jump when it arrives.
          <img
            src={wordmark}
            alt={ladder.name}
            width={1446}
            height={734}
            className={`h-auto w-full ${size === "lg" ? "max-w-[220px] sm:max-w-[200px]" : "max-w-[180px] sm:max-w-[160px]"}`}
          />
        ) : (
          <p className="font-display text-2xl font-bold uppercase leading-none tracking-tight text-sanur-orange sm:text-3xl">
            {ladder.name}
          </p>
        )}
        {/* The countdown is colour-agnostic and inherits from here. */}
        <div className="text-sanur-orange [&_p]:text-center sm:[&_p]:text-left">
          <RegistrationCountdown
            closesAt={closesAt}
            label="Registration closes in"
            onExpire={() => setExpiredFor(closesAt)}
          />
        </div>
        <Button asChild className="gap-1.5 bg-sanur-orange text-white hover:bg-sanur-orange/90 sm:shrink-0">
          <Link to={`/ladders/${ladder.slug}/register`}>
            Register
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
};
